import { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, MapPin, PackageCheck } from 'lucide-react';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import type { HandoverResultDto } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { AuthImage } from '../../../shared/ui/AuthImage.js';
import { GeoLink } from '../../../shared/ui/GeoLink.js';
import { PhotoLightbox, type LightboxPhoto } from '../../../shared/ui/PhotoLightbox.js';
import { requisitionErrorText } from '../../requisition/model/requisition-error.js';
import { useGeolocation } from '../model/useGeolocation.js';
import { usePhotoDrafts } from '../model/usePhotoDrafts.js';
import { useConfirmReceipt, useHandovers } from '../model/handover.queries.js';
import { PhotoGrid } from './PhotoGrid.js';
import { SignatureSetup } from '../../signature/ui/SignatureSetup.js';
import { PhotoPicker } from './PhotoPicker.js';
import { StoreroomPicker, type StoreroomPlace } from '../../subdivision/ui/StoreroomPicker.js';
import { ReceiptPlacementList, type PlacementSpot } from '../../subdivision/ui/ReceiptPlacementList.js';
import { useMyStorerooms } from '../../subdivision/model/subdivision.queries.js';
import { useRequisition } from '../../requisition/model/requisition.queries.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';

/**
 * QABUL QILISH oynasi — talabnomani yopadigan qadam.
 *
 * Nega umumiy o'tish tugmasi yetmaydi: zanjirning oxirgi bo'g'ini dalilsiz
 * qolardi. Skladchining foto-hisoboti u nimani BERGANINI ko'rsatadi,
 * buyurtmachi nimani OLGANINI emas — "berdim / olmadim" bahsida esa aynan
 * shu farq hal qiladi.
 *
 * Rasm MAJBURIY va bu server qoidasi (`confirm-receipt` rasmsiz 400 beradi,
 * umumiy o'tish esa `RECEIPT_CONFIRMED` ga umuman yo'l qo'ymaydi). Oyna
 * qoidani takrorlaydi, lekin ushlab turmaydi: faqat ekranda turgan qoidani
 * oddiy HTTP so'rov bilan chetlab o'tish mumkin bo'lardi.
 *
 * Tugma jim o'chib qolmaydi — yonida SABABI yozilgan. "Ishlamayapti" bilan
 * "rasm kerak" o'rtasidagi farq ish o'rnida juda katta.
 */
export interface ReceiptDialogProps {
  open: boolean;
  requisitionId: string;
  onClose: () => void;
  onDone: (result: HandoverResultDto) => void;
  /**
   * Oyna ochilishidan OLDIN olingan suratlar.
   *
   * "Qabul qildim" tugmasining O'ZI fayl maydoni (`TransitionBar` dagi
   * `slot`): bir tegishda kamera ochiladi va oyna surat bilan birga
   * keladi. Ikki marta tegish — kamerani ochish uchun yana bir tugma —
   * qo'lqopdagi barmoq va sovuq ombor uchun ortiqcha qadam bo'lardi.
   */
  initialFiles?: readonly File[];
}

export const ReceiptDialog = ({
  open,
  requisitionId,
  onClose,
  onDone,
  initialFiles,
}: ReceiptDialogProps) => {
  const drafts = usePhotoDrafts('HANDOVER_PHOTO');
  const [comment, setComment] = useState('');
  const [place, setPlace] = useState<StoreroomPlace | null>(null);
  const confirm = useConfirmReceipt(requisitionId);

  /*
   * TAQSIMLASH — «hammasi bittaga» yoki «mahsulot bo'yicha».
   *
   * Standart holat bittaga: aksariyat qabulda bitta-ikkita satr bo'ladi
   * va har biriga alohida ombor tanlash bekor mehnat bo'lardi. Ajratish
   * KERAK bo'lganda esa u bir tegishda ochiladi — ilgari buning yagona
   * yo'li hammasini bitta omborga olib, keyin bittalab ko'chirish edi.
   */
  const [split, setSplit] = useState(false);
  const [places, setPlaces] = useState<ReadonlyMap<string, PlacementSpot[]>>(new Map());

  const detail = useRequisition(open ? requisitionId : null);
  const rooms = useMyStorerooms(open);
  const nomenclatures = useNomenclatures({ limit: 200 });
  const nameOf = (id: string): { name: string; unit: string } | undefined => {
    const found = (nomenclatures.data?.items ?? []).find((item) => item.id === id);
    return found === undefined ? undefined : { name: found.name, unit: found.unit };
  };

  const lines = detail.data?.lines ?? [];
  const activeRooms = (rooms.data ?? []).filter((room) => room.isActive);
  /*
   * Ajratish BITTA satrda ham ma'noli.
   *
   * Ilgari shart «ikkitadan ko'p satr» edi va bu xato edi: kelgan o'nta
   * paneldan oltitasini garajga, to'rttasini seyfga qo'yish aynan BITTA
   * satrli talabnomada kerak bo'ladi. Shu sababli bitta mahsulotli
   * qabulda tanlov umuman ko'rinmasdi.
   *
   * Yagona haqiqiy shart — bo'linmada kamida IKKITA ombor bo'lishi:
   * bittasi bo'lsa bo'ladigan joy bitta va tanlovning o'zi yo'q.
   */
  const canSplit = lines.some((line) => line.placements.length > 0) && activeRooms.length > 1;

  /* Ajratish yoqilganda har satr joriy (umumiy) joydan boshlanadi —
     odam faqat FARQ qiladiganini o'zgartiradi. */
  const setSplitOn = (next: boolean): void => {
    setSplit(next);
    if (!next) return;
    /* Har satr joriy (umumiy) joydan BITTA manzil bilan boshlanadi —
       odam faqat farq qiladiganini o'zgartiradi yoki «yana bir joyga»
       bosib miqdorni bo'ladi. */
    const start: StoreroomPlace = place ?? {
      storeroomId: activeRooms[0]?.id ?? '',
      zoneId: null,
    };
    if (start.storeroomId === '') return;
    setPlaces(
      new Map(
        /* HAR PARTIYA uchun bittadan yozuv: bitta satr bir nechta
           katakdan to'lgan bo'lsa, bo'linmaga ham shuncha lot tushadi. */
        lines.flatMap((line) =>
          line.placements.map((place) => [place.batchId, [{ ...start, qty: null }]] as const),
        ),
      ),
    );
  };

  /*
   * Koordinata IKKI manbadan izlanadi va ular bir-birining o'rnini
   * bosmaydi:
   *
   *   - brauzerniki — aniqroq, lekin FAQAT HTTPS da beriladi;
   *   - rasmning EXIF i — `http://` da ham ishlaydi, chunki koordinata
   *     brauzerdan emas, fayldan keladi.
   *
   * Tizim ichki tarmoqda `http://` bilan ochilgani uchun amalda ikkinchisi
   * ishlaydi; birinchisi esa HTTPS ga o'tilgan kuni o'z-o'zidan ishlab
   * ketadi va kod o'zgarmaydi.
   */
  const browserGps = useGeolocation(open);
  const gps = browserGps ?? drafts.gps;

  /*
   * BERISH dalolatnomasining suratlari — skladchi nimani bergani.
   *
   * Oyna yopiq turganda so'ralmaydi (`null`): talabnomalar ro'yxatida o'nlab
   * yopiq oyna bo'lishi mumkin va har biri o'z so'rovini yuborsa, sahifa
   * ochilishi bir nechta keraksiz so'rovga aylanardi.
   */
  const acts = useHandovers(open ? requisitionId : null);
  const issuedPhotos: LightboxPhoto[] = (acts.data ?? [])
    .filter((act) => act.kind === 'ISSUE')
    .flatMap((act) => act.photos.map((photo) => ({ id: photo.fileId, url: photo.url, gps: photo.gps })));
  const [opened, setOpened] = useState<number | null>(null);

  /*
   * Tugmani bosgan payt olingan surat oynaga O'ZI kiradi.
   *
   * `initialFiles` — havola (`readonly File[]`): har yangi tanlov yangi
   * massiv beradi. Lekin BOG'LIQLIK RO'YXATINING O'ZI yetarli emas —
   * effekt bir xil havola bilan qayta ishga tushishi mumkin (`StrictMode`
   * uni ataylab ikki marta chaqiradi), `pick` esa yuklashni BOSHLAB
   * yuboradigan amal. Natijada bitta surat ikki marta yuklanib, oynada
   * ikkita qoralama qolardi: birinchisining `blob:` havolasi tozalashda
   * bekor qilinib «Yuklanmadi» bo'lib ko'rinardi, ikkinchisi esa o'sha
   * rasmni dalolatnomaga ikkinchi marta biriktirardi.
   *
   * Shuning uchun ISHLATILGAN massiv eslab qolinadi: har tanlov aynan bir
   * marta qoralamaga tushadi.
   */
  const consumed = useRef<readonly File[] | null>(null);

  useEffect(() => {
    if (!open || initialFiles === undefined || initialFiles.length === 0) return;
    if (consumed.current === initialFiles) return;
    consumed.current = initialFiles;
    drafts.pick([...initialFiles]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `drafts` har renderda yangi obyekt; qoralamani qayta yuklash kerak emas.
  }, [open, initialFiles]);

  const close = (): void => {
    drafts.reset();
    setComment('');
    setPlace(null);
    setSplit(false);
    setPlaces(new Map());
    confirm.reset();
    onClose();
  };

  /*
   * To'siqlar TARTIBI — eng birinchi hal qilinadigani birinchi.
   *
   * Joylashuv eng oxirida: rasm yuklanmaguncha uning koordinatasi ham
   * ma'lum bo'lmaydi, ya'ni undan oldin ko'rsatilsa, xabar bir zumda
   * paydo bo'lib, yo'qolib turardi.
   */
  const blocker =
    drafts.photos.length === 0
      ? t('handover.receiptNeedsPhoto')
      : drafts.uploading
        ? t('handover.errorPhotoPending')
        : drafts.readyIds.length === 0
          ? t('handover.receiptAllFailed')
          : gps === null
            ? t('handover.receiptNeedsGps')
            : null;

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('handover.receiptTitle')}
      description={t('handover.receiptHint')}
      size="lg"
      /* Rasm allaqachon yuklangan bo'lsa, oyna tasodifan yopilmasin. */
      dirty={drafts.photos.length > 0}
      busy={confirm.isPending}
      error={
        confirm.error === null || confirm.error === undefined
          ? undefined
          : requisitionErrorText(confirm.error, t('handover.receiptFailed'))
      }
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          {blocker === null ? null : (
            <span className="mr-auto text-sm text-text-muted">{blocker}</span>
          )}
          <Button variant="ghost" onClick={close} disabled={confirm.isPending}>
            {t('handover.receiptCancel')}
          </Button>
          <Button
            loading={confirm.isPending}
            disabled={blocker !== null}
            onClick={() =>
              confirm.mutate(
                {
                  photoFileIds: drafts.readyIds,
                  /* Brauzer bergan bo'lsa yuboriladi; bermasa server uni
                     rasmning EXIF idan o'zi oladi. */
                  ...(browserGps === null ? {} : { gps: browserGps }),
                  ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
                  /* Umumiy joy HAR DOIM yuboriladi: ajratilmagan
                     satrlar (agar qolsa) o'shanga tushadi — server
                     qoidasi shunday (`applyPlacements`). */
                  ...(place === null ? {} : { storeroom: place }),
                  ...(split && places.size > 0
                    ? {
                        placements: [...places].flatMap(([batchId, spots]) =>
                          spots.map((spot) => ({
                            batchId,
                            storeroomId: spot.storeroomId,
                            zoneId: spot.zoneId,
                            /* Oxirgi manzil QOLDIQNI oladi — miqdori
                               yuborilmaydi (server hisoblaydi). */
                            ...(spot.qty === null ? {} : { qty: spot.qty }),
                          })),
                        ),
                      }
                    : {}),
                },
                {
                  onSuccess: (result) => {
                    drafts.reset();
                    onDone(result);
                  },
                },
              )
            }
          >
            <PackageCheck size={16} aria-hidden />
            {t('handover.receiptSubmit')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Nima suratga olinishi kerakligi AYTILADI: "rasm yuklang" degan
            ko'rsatma odamni qutining emas, qog'ozning suratini olishga ham
            olib borishi mumkin. */}
        <p
          className={cn(
            'rounded-[var(--radius-sm)] border border-accent/25 bg-accent-soft',
            'px-3 py-2.5 text-sm text-text',
          )}
        >
          {t('handover.receiptWhatToPhoto')}
        </p>

        {/*
          SKLADCHI BERGANI — qabul qilayotgan odam nimani olayotganini
          suratdan ko'radi. Ilgari bu rasm faqat talabnoma sahifasining
          pastida turardi: solishtirish uchun oynani yopish kerak bo'lardi,
          ya'ni aynan taqqoslash payti dalil ko'zdan g'oyib bo'lardi.

          Bo'lim FAQAT O'QISH uchun: o'chirish tugmasi yo'q, chunki bu
          boshqa odamning yozib bo'lgan dalolatnomasi.
        */}
        <section className="flex flex-col gap-2" aria-label={t('handover.receiptIssuedTitle')}>
          <header className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-text">{t('handover.receiptIssuedTitle')}</h3>
            <p className="text-xs text-text-muted">{t('handover.receiptIssuedHint')}</p>
          </header>

          {issuedPhotos.length === 0 ? (
            <p className="text-sm text-text-faint">{t('handover.receiptIssuedNone')}</p>
          ) : (
            <ul className="grid grid-cols-3 gap-3 md:grid-cols-5 xl:grid-cols-6">
              {issuedPhotos.map((photo, index) => (
                <li key={photo.id}>
                  <button
                    type="button"
                    onClick={() => setOpened(index)}
                    title={t('handover.actPhotoOpen')}
                    className={cn(
                      'block w-full overflow-hidden rounded-[var(--radius-md)] border border-border',
                      'transition-[border-color] duration-[var(--motion-fast)]',
                      'hover:border-accent focus-visible:border-accent focus-visible:outline-none',
                    )}
                  >
                    <AuthImage
                      src={photo.url}
                      alt={t('handover.actPhotoAlt', { index: index + 1 })}
                      className="aspect-square w-full object-cover"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-2" aria-label={t('handover.receiptMineTitle')}>
          <header className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-text">{t('handover.receiptMineTitle')}</h3>
            <p className="text-xs text-text-muted">{t('handover.receiptMineHint')}</p>
          </header>

          <div className="flex flex-wrap items-center gap-2">
            <PhotoPicker
              label={t('handover.photoCamera')}
              icon={<Camera size={16} aria-hidden />}
              capture="environment"
              tone="primary"
              onPick={drafts.pick}
            />
            <PhotoPicker
              label={t('handover.photoPick')}
              icon={<ImagePlus size={16} aria-hidden />}
              onPick={drafts.pick}
            />
            {drafts.photos.length === 0 ? null : (
              <span className="tabular text-sm text-text-muted">
                {t('handover.photoCount', { count: drafts.photos.length })}
              </span>
            )}
          </div>

          <PhotoGrid photos={drafts.photos} onRemove={drafts.remove} />
        </section>

        <PhotoLightbox
          photos={issuedPhotos}
          index={opened}
          onIndexChange={setOpened}
          onClose={() => setOpened(null)}
        />

        {/* QAYERGA — mahsulot bo'linmaning qaysi ombori va zonasiga tushadi. */}
        <div className="flex flex-col gap-3">
          {canSplit ? (
            <div role="radiogroup" aria-label={t('storeroom.splitLabel')} className="flex flex-wrap gap-2">
              {[
                { on: false, label: t('storeroom.splitOff') },
                { on: true, label: t('storeroom.splitOn') },
              ].map((option) => (
                <button
                  key={String(option.on)}
                  type="button"
                  role="radio"
                  aria-checked={split === option.on}
                  onClick={() => setSplitOn(option.on)}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm',
                    'transition-colors duration-[var(--motion-fast)]',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                    split === option.on
                      ? 'border-accent bg-accent text-[var(--color-accent-contrast)]'
                      : 'border-border bg-surface text-text hover:border-border-strong',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}

          {split ? (
            <section className="flex flex-col gap-2" aria-label={t('storeroom.splitOn')}>
              <p className="text-xs text-text-muted">{t('storeroom.splitHint')}</p>
              <ReceiptPlacementList
                lines={lines}
                rooms={activeRooms}
                value={places}
                onChange={(batchId, spots) =>
                  setPlaces((prev) => new Map(prev).set(batchId, spots))
                }
                nameOf={nameOf}
              />
            </section>
          ) : (
            <StoreroomPicker value={place} onChange={setPlace} />
          )}
        </div>

        {/* Imzo — rasmdan KEYIN: avval «nimani qabul qilyapman», keyin
            «kim qabul qildi». Imzosi bor xodimga bu bo'lim ko'rinmaydi. */}
        <SignatureSetup />

        {/*
          QABUL JOYI — hujjatning bir qismi, shuning uchun u tasdiqlashdan
          OLDIN ko'rinadi. Odam nimani imzolayotganini bilishi kerak.
        */}
        {drafts.photos.length === 0 ? null : (
          <p
            className={cn(
              'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[var(--radius-sm)] border px-3 py-2 text-sm',
              gps === null
                ? 'border-warn/25 bg-warn-soft text-warn'
                : 'border-border bg-surface-2 text-text-muted',
            )}
          >
            <MapPin size={14} aria-hidden />
            {gps === null ? (
              t('handover.receiptGpsMissing')
            ) : (
              <>
                {t('handover.receiptGpsFound')}
                <GeoLink gps={gps} />
              </>
            )}
          </p>
        )}

        {/* Izoh IXTIYORIY: kam chiqqan, shikastlangan yoki boshqacha
            yetkazilgan bo'lsa — buni yozadigan joy bo'lishi kerak, aks holda
            e'tiroz hujjatga umuman tushmay qolardi. */}
        <Input
          label={t('handover.receiptComment')}
          hint={t('handover.receiptCommentHint')}
          maxLength={500}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </div>
    </Dialog>
  );
};
